import React, { useState, useCallback } from "react";
import { View, StyleSheet, Pressable, ScrollView, TouchableOpacity } from "react-native";
import { Menu } from "react-native-paper";
import CustomText from "../common/general/CustomText/CustomText";
import CustomButton from "../common/general/CustomButton/CustomButton";
import Pill from "../common/Pill/Pill";
import { CourseGenerationStatus, useGenerateCourseExportMutation } from "../../graphql/generated/graphql";
import { RolePermissions } from "../../constants/rolesAndPermissions";
import { useNavigation } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import type { RootStackParamList } from "../../navigation/navigation.types";
import { Plus } from "@phosphor-icons/react/dist/csr/Plus";
import { CaretDown } from "@phosphor-icons/react/dist/csr/CaretDown";
import { CaretUp } from "@phosphor-icons/react/dist/csr/CaretUp";
import { ArrowSquareOut } from "@phosphor-icons/react/dist/csr/ArrowSquareOut";
import { GestureResponderEvent } from "react-native";
import { CourseInformationTab } from "../../views/adminViews/CourseInformationView";
import { useAuth } from "../../hooks/useAuth";
import { UserRoleUI } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/Toast/ToastProvider";

export enum ManageCoursesModalState {
  CLOSED = "CLOSED",
  DELETE_COURSE = "DELETE_COURSE",
  DELETE_CONFIRMATION = "DELETE_CONFIRMATION",
  ASSIGN_FACULTY = "ASSIGN_FACULTY",
  EDIT_FACULTY = "EDIT_FACULTY",
  COURSE_ROSTER = "COURSE_ROSTER",
  CLONE_COURSE = "CLONE_COURSE",
  CLONE_COURSE_CONFIRMATION = "CLONE_COURSE_CONFIRMATION",
  PROCESSING_CLONE_COURSE = "PROCESSING_CLONE_COURSE",
  EDIT_COURSE = "EDIT_COURSE",
  ADD_COURSE = "ADD_COURSE",
  BULK_UPLOAD_COURSE = "BULK_UPLOAD_COURSE",
  BULK_ASSESSMENT_PROCESSING = "BULK_ASSESSMENT_PROCESSING",
  BULK_ASSESSMENT_CONFIRMATION = "BULK_ASSESSMENT_CONFIRMATION",
}

interface Course {
  id: string;
  name?: string | null;
  course_code?: string | null;
  generationStatus?: CourseGenerationStatus | null;
  isDraft?: boolean | null;
  isEnrollmentAvailable?: boolean | null;
  teachingFaculty: Array<{
    id: string;
    name: string;
  }>;
  enrolledStudents: Array<{
    id: string;
    name: string;
  }>;
  degreesHasCourse: Array<{
    id: string;
    name?: string;
  }>;
}
interface ManageCoursesTableProps {
  courses: Course[];
  onActionModalOpen: (
    courseId: string,
    courseName: string,
    modalType: ManageCoursesModalState,
    isCourseProcessing: boolean
  ) => void;
  hasDegrees: boolean;
  returnTo?: string;
}

type MenuState = {
  visible: boolean;
  x: number;
  y: number;
  courseId: string;
  courseName: string;
  isCourseProcessing: boolean;
  canEditCourse: boolean;
};

const ManageCoursesTable: React.FC<ManageCoursesTableProps> = ({
  courses,
  onActionModalOpen,
  hasDegrees,
  returnTo = "Manage Courses",
}) => {
  const { userInfo } = useAuth();

  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const [generateCourseExport] = useGenerateCourseExportMutation();

  const isAuditorOrAbove = userInfo.userRole === UserRoleUI.AUDITOR || userInfo.userRole === UserRoleUI.ADMIN;

  const [menuState, setMenuState] = useState<MenuState>({
    visible: false,
    x: 0,
    y: 0,
    courseId: null,
    courseName: null,
    isCourseProcessing: false,
    canEditCourse: false,
  });

  const formatCourseNameWithCode = (courseName: string, courseCode?: string | null): string => {
    if (courseCode && courseCode.trim()) {
      return `${courseCode} ${courseName}`;
    }
    return courseName;
  };

  const isCourseProcessing = (status: CourseGenerationStatus) => {
    return !(
      status === null ||
      status === CourseGenerationStatus.Completed ||
      status === CourseGenerationStatus.Failed ||
      status === CourseGenerationStatus.GeneratingImageCompleted ||
      status === CourseGenerationStatus.GeneratingImageFailed ||
      status === CourseGenerationStatus.GeneratingImage
    );
  };

  const handleCourseClick = (courseId: string) => {
    const selectedCourse = courses.find((course) => course.id === courseId);
    const courseName = formatCourseNameWithCode(selectedCourse?.name, selectedCourse?.course_code);

    navigation.navigate("Course Information", {
      courseId: courseId,
      courseName,
      returnTo: returnTo,
    });
  };

  const handleOpenMenu = useCallback(
    (event: GestureResponderEvent, course: Course, isTeachingThisCourse: boolean) => {
      const { pageX, pageY } = event.nativeEvent;

      setMenuState({
        visible: true,
        x: pageX - 100,
        y: pageY,
        courseId: course.id,
        courseName: formatCourseNameWithCode(course.name, course.course_code),
        isCourseProcessing: isCourseProcessing(course.generationStatus),
        canEditCourse:
          (isAuditorOrAbove || isTeachingThisCourse) && userInfo.userPermissions.includes(RolePermissions.courseEdit),
      });
    },
    [isAuditorOrAbove, userInfo.userPermissions]
  );

  const handleCloseMenu = useCallback(() => {
    setMenuState({
      visible: false,
      courseId: null,
      courseName: null,
      x: 0,
      y: 0,
      isCourseProcessing: false,
      canEditCourse: false,
    });
  }, []);

  const handleActionModalOpen = (modalType: ManageCoursesModalState) => {
    handleCloseMenu();
    onActionModalOpen(menuState.courseId, menuState.courseName, modalType, menuState.isCourseProcessing);
  };

  const handleViewCourse = (courseId: string) => {
    const selectedCourse = courses.find((course) => course.id === courseId);
    const courseName = formatCourseNameWithCode(selectedCourse?.name, selectedCourse?.course_code);

    handleCloseMenu();
    navigation.navigate("Custom Coursework Modules", {
      courseId,
      courseName,
      returnTo: returnTo,
    });
  };

  const handleViewRoster = (courseId: string) => {
    const selectedCourse = courses.find((course) => course.id === courseId);
    const courseName = formatCourseNameWithCode(selectedCourse?.name, selectedCourse?.course_code);

    handleCloseMenu();
    navigation.navigate("Course Information", {
      courseId,
      courseName,
      returnTo: returnTo,
      activeTab: CourseInformationTab.ROSTER_MANAGEMENT,
    });
  };

  const handleViewCourseInformation = (courseId: string) => {
    const selectedCourse = courses.find((course) => course.id === courseId);
    const courseName = formatCourseNameWithCode(selectedCourse?.name, selectedCourse?.course_code);

    handleCloseMenu();
    navigation.navigate("Course Information", {
      courseId,
      courseName,
      returnTo: returnTo,
      activeTab: CourseInformationTab.COURSE_CATALOG_INFO,
    });
  };

  const { showToast } = useToast();

  const handleExportCourseDetails = async (courseId: string) => {
    await generateCourseExport({ variables: { courseId } });
    showToast({
      message: "Generating course details... You will be notified in when download link is ready via notification.",
      type: "info",
    });
    handleCloseMenu();
  };

  const getCourseStatusPill = (course: Course) => {
    if (course.isDraft) {
      return { text: "DRAFT", backgroundColor: "#FEF3C7", textColor: "#92400E" };
    }
    if (course.isEnrollmentAvailable) {
      return { text: "PUBLISHED", backgroundColor: "#D1FAE5", textColor: "#065F46" };
    }
    return { text: "INACTIVE", backgroundColor: "#E5E7EB", textColor: "#6B7280" };
  };

  return (
    <>
      <ScrollView style={styles.tableContainer} tabIndex={0}>
        <View style={styles.table}>
          {/* Table Header */}
          <View style={styles.tableHeader}>
            <View style={[styles.cell, styles.courseNameCell]}>
              <CustomText text="Course Name" size="s" weight="medium" style={styles.headerText} />
            </View>
            <View style={[styles.cell, styles.statusCell]}>
              <CustomText text="Course Status" size="s" weight="medium" style={styles.headerText} />
            </View>
            <View style={[styles.cell, styles.facultyCell]}>
              <CustomText text="Assigned Faculty" size="s" weight="medium" style={styles.headerText} />
            </View>
            {hasDegrees && (
              <View style={[styles.cell, styles.degreesCell]}>
                <CustomText text="Degrees" size="s" weight="medium" style={styles.headerText} />
              </View>
            )}
            <View style={[styles.cell, styles.enrollmentCell]}>
              <CustomText text="Current Enrollment" size="s" weight="medium" style={styles.headerText} />
            </View>
            <View style={[styles.cell, styles.actionsCell]}>
              <CustomText text="Actions" size="s" weight="medium" style={styles.headerText} />
            </View>
          </View>

          {/* Table Body */}
          {courses.map((course) => {
            const isTeachingThisCourse = course.teachingFaculty.some((faculty) => faculty.id === userInfo.userId);
            const displayName = course?.name;
            const courseCode = course?.course_code;
            const statusPill = getCourseStatusPill(course);
            const isMenuOpen = menuState.visible && menuState.courseId === course.id;

            return (
              <View key={course.id} style={styles.tableRow}>
                <View style={[styles.cell, styles.courseNameCell]}>
                  <View style={styles.courseNameContent}>
                    <TouchableOpacity style={styles.courseNameContainer} onPress={() => handleCourseClick(course.id)}>
                      <CustomText
                        text={displayName}
                        weight="medium"
                        style={styles.courseName}
                        useTranslationText={false}
                        numberOfLines={2}
                        ellipsizeMode="tail"
                      />
                      <ArrowSquareOut size={16} color="#3E68FE" />
                    </TouchableOpacity>
                    <CustomText
                      weight="medium"
                      size="xs"
                      useTranslationText={false}
                      text={courseCode}
                      style={styles.courseCodeName}
                    />
                    {isCourseProcessing(course.generationStatus) && (
                      <CustomText
                        text="(Currently Processing)"
                        size="xs"
                        style={styles.processingText}
                        useTranslationText={false}
                      />
                    )}
                  </View>
                </View>

                <View style={[styles.cell, styles.statusCell]}>
                  <View style={styles.pillContainer}>
                    <Pill
                      text={statusPill.text}
                      backgroundColor={statusPill.backgroundColor}
                      textColor={statusPill.textColor}
                    />
                  </View>
                </View>

                <View style={[styles.cell, styles.facultyCell]}>
                  {course.teachingFaculty.length > 0 ? (
                    <Pressable
                      onPress={() =>
                        onActionModalOpen(
                          course.id,
                          course.name,
                          ManageCoursesModalState.EDIT_FACULTY,
                          isCourseProcessing(course.generationStatus)
                        )
                      }
                      style={styles.facultyList}>
                      {course.teachingFaculty.map((faculty, index) => (
                        <View key={faculty.id} style={styles.facultyItem}>
                          <CustomText
                            text={faculty.name}
                            size="s"
                            style={styles.facultyName}
                            useTranslationText={false}
                            weight="medium"
                          />
                          {index < course.teachingFaculty.length - 1 && (
                            <CustomText text=", " size="s" style={styles.facultyName} useTranslationText={false} />
                          )}
                        </View>
                      ))}
                    </Pressable>
                  ) : (
                    <CustomButton
                      text="Assign Faculty"
                      size="s"
                      leftIcon={<Plus size={14} color="#344054" />}
                      onPress={() =>
                        onActionModalOpen(
                          course.id,
                          formatCourseNameWithCode(course.name, course.course_code),
                          ManageCoursesModalState.ASSIGN_FACULTY,
                          isCourseProcessing(course.generationStatus)
                        )
                      }
                      useTranslationText={false}
                      style={styles.assignFacultyButton}
                      textStyle={{ fontSize: 13, color: "#475569" }}
                    />
                  )}
                </View>

                {hasDegrees && (
                  <View style={[styles.cell, styles.degreesCell]}>
                    <View style={styles.degreesList}>
                      {course.degreesHasCourse.map((degree, index) => (
                        <View key={degree.id} style={styles.pillContainer}>
                          <Pill text={degree.name} backgroundColor="#E0F2FE" textColor="#0C4A6E" />
                        </View>
                      ))}
                    </View>
                  </View>
                )}

                <View style={[styles.cell, styles.enrollmentCell]}>
                  <CustomText
                    text={course.enrolledStudents.length.toLocaleString()}
                    size="m"
                    weight="bold"
                    style={styles.enrollmentText}
                    useTranslationText={false}
                  />
                </View>

                <View style={[styles.cell, styles.actionsCell]}>
                  <CustomButton
                    text="Manage"
                    size="s"
                    rightIcon={
                      isMenuOpen ? <CaretUp size={14} color="#FFFFFF" /> : <CaretDown size={14} color="#475569" />
                    }
                    onPress={(e) => handleOpenMenu(e, course, isTeachingThisCourse)}
                    useTranslationText={false}
                    styleType="transparent"
                    style={[styles.manageButton, isMenuOpen && styles.manageButtonActive]}
                    textStyle={[styles.manageText, isMenuOpen && styles.manageTextActive]}
                  />
                </View>
              </View>
            );
          })}
        </View>
      </ScrollView>

      <Menu
        visible={menuState.visible}
        onDismiss={handleCloseMenu}
        anchor={{ x: menuState.x, y: menuState.y }}
        contentStyle={styles.menu}>
        <Menu.Item
          disabled={menuState.isCourseProcessing || !menuState.canEditCourse}
          onPress={() => handleViewCourseInformation(menuState.courseId)}
          title="Edit Course Information"
          titleStyle={[
            styles.menuItemText,
            (!menuState.canEditCourse || menuState.isCourseProcessing) && styles.disabledText,
          ]}
        />
        <Menu.Item
          disabled={menuState.isCourseProcessing || !menuState.canEditCourse}
          onPress={() => handleViewCourse(menuState.courseId)}
          title="Edit Course Content"
          titleStyle={[
            styles.menuItemText,
            (!menuState.canEditCourse || menuState.isCourseProcessing) && styles.disabledText,
          ]}
        />
        <Menu.Item
          disabled={!menuState.canEditCourse}
          onPress={() => handleViewRoster(menuState.courseId)}
          title="Manage Roster"
          titleStyle={[styles.menuItemText, !menuState.canEditCourse && styles.disabledText]}
        />
        <Menu.Item
          disabled={menuState.isCourseProcessing}
          onPress={() => handleActionModalOpen(ManageCoursesModalState.CLONE_COURSE)}
          title="Clone Course"
          titleStyle={[styles.menuItemText, menuState.isCourseProcessing && styles.disabledText]}
        />
        <Menu.Item
          disabled={menuState.isCourseProcessing || !menuState.canEditCourse}
          onPress={() => {
            handleExportCourseDetails(menuState.courseId);
          }}
          title="Export Course"
          titleStyle={[styles.menuItemText]}
        />
        <View style={styles.menuDivider} />
        <Menu.Item
          disabled={menuState.isCourseProcessing || !menuState.canEditCourse}
          onPress={() => handleActionModalOpen(ManageCoursesModalState.DELETE_COURSE)}
          title="Delete Course"
          titleStyle={[
            styles.menuItemText,
            styles.deleteText,
            (!menuState.canEditCourse || menuState.isCourseProcessing) && styles.disabledText,
          ]}
        />
      </Menu>
    </>
  );
};

const styles = StyleSheet.create({
  tableContainer: {
    flex: 1,
  },
  table: {
    backgroundColor: "#fff",
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#F9FAFB",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    paddingVertical: 12,
    paddingHorizontal: 24,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    paddingVertical: 16,
    paddingHorizontal: 24,
    minHeight: 72,
  },
  cell: {
    justifyContent: "center",
    paddingHorizontal: 8,
  },
  courseNameCell: {
    flex: 3,
  },
  statusCell: {
    flex: 1.5,
    alignItems: "flex-start",
  },
  facultyCell: {
    flex: 2.5,
    alignItems: "flex-start",
  },
  degreesCell: {
    flex: 2.5,
    alignItems: "flex-start",
  },
  enrollmentCell: {
    flex: 1.25,
  },
  actionsCell: {
    flex: 1.25,
    alignItems: "flex-start",
  },
  headerText: {
    color: "#6B7280",
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  courseNameContent: {
    gap: 4,
  },
  courseNameContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  courseName: {
    color: "#3E68FE",
    fontSize: 15,
    cursor: "pointer",
  },
  processingText: {
    color: "#6B7280",
    fontStyle: "italic",
  },
  pillContainer: {
    alignSelf: "flex-start",
  },
  facultyList: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  facultyItem: {
    flexDirection: "row",
  },
  facultyName: {
    color: "#101828",
  },
  degreesList: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 4,
  },
  enrollmentText: {
    color: "#1E293B",
  },
  manageButton: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderColor: "#EAECF0",
    backgroundColor: "#F9FAFB",
    borderWidth: 1,
    borderRadius: 6,
  },
  manageText: {
    color: "#475569",
    fontSize: 13,
  },
  manageTextActive: {
    color: "#FFFFFF",
    fontSize: 13,
  },
  menu: {
    backgroundColor: "#fff",
    minWidth: 200,
  },
  menuItemText: {
    fontSize: 14,
    color: "#374151",
  },
  disabledText: {
    color: "#9CA3AF",
  },
  deleteText: {
    color: "#DC2626",
  },
  menuDivider: {
    height: 1,
    backgroundColor: "#E5E7EB",
    marginVertical: 4,
  },
  assignFacultyButton: {
    backgroundColor: "#F9FAFB",
    borderColor: "#EAECF0",
    borderWidth: 1,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
  },
  courseCodeName: {
    color: "#667085",
  },
  manageButtonActive: {
    backgroundColor: "#3E68FE",
  },
});

export default ManageCoursesTable;
